import React, {useEffect} from 'react';
import {BrowserRouter as Router, Route, Routes, useLocation, useNavigate} from 'react-router-dom';
import './scss/base.scss';
import {ChatList} from "./components/layout/ChatList";
import {Inbox as InboxPage} from "./routes/Inbox";
import {Login} from "./routes/Login";
import {Sidebar} from "./components/layout/Sidebar";
import {logout, useUserContext} from "./providers/UserContext";
import {getAuth, onAuthStateChanged, User} from "firebase/auth";
import {addWorkspace, getRef, joinWorkspace} from "./lib/database";
import {Workspace} from "./lib/types";
import {onValue} from "firebase/database";
import {Group} from "./routes/Group";
import {Dm} from "./routes/Dm";
import {ChatPage} from "./routes/Chat";
import {Compose} from "./routes/Compose";
import {useInboxContext} from "./providers/InboxProvider";
import classNames from "classnames";
import {useChatListContext} from "./providers/ChatListProvider";
import {useModalContext} from "./providers/ModalProvider";
import {Pings} from "./routes/Pings";
import Modal from "./components/common/Modal";
import {Test} from "./routes/Test";
import {FunctionCaller} from "./routes/FunctionCaller";
import {Chats} from "./routes/Chats";
import {useLayoutContext} from "./providers/LayoutProvider";
import useRepeatRefresh from "./lib/hooks/useConditionalRefresh";
import {processUserAvatar} from "./lib/utils/files";
function  App() {
  const { user, setUser, activeWorkspace, setActiveWorkspace,activeChatId } = useUserContext();
  const { inboxVisible, setInboxVisible } = useInboxContext();
  const { chatListVisible } = useChatListContext();
  const navigate = useNavigate();
  const auth = getAuth();
  auth.languageCode = 'en';
  const [ready, setReady] = React.useState(false);
  useRepeatRefresh(4);
  useEffect(() => {
    onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUser(user);
        interface Workspaces {
          [key: string]: Workspace
        }
        const workspaces: Workspaces = {
          "nima": {
            id: 'nima',
            name: 'Nima',
            members: {},
          },
          "kevi": {
            id: 'kevi',
            name: 'Kevi',
            members: {},
          },
          "thecontext": {
            id: 'thecontext',
            name: 'The context',
            members: {},
          },
          "thecontextnew": {
            id: 'thecontextnew',
            name: 'The context',
            members: {},
          },
        }
        const emailWhitelist: {[key: string]: keyof Workspaces} = {
          "gabriel@nima.chat": "nima",
          "gabriel@forum.co": "nima",
          "industriasciclosc@gmail.com": "nima",
          "joaopvilla1@gmail.com": "nima",
          "salar.kamangar@gmail.com": "nima",
          "onassar@gmail.com": "nima",
          "oliver@forum.co": "nima",
          "gab12.floripa@gmail.com": "thecontextnew",
          "gabriel@kevi.io": "kevi",
          "vinicius@kevi.io": "kevi",
          "salar@thecontext.org": "thecontextnew",
          "devon@thecontext.org": "thecontextnew",
          "seth@thecontext.org": "thecontextnew",
          "devon.magliozzi@thecontext.org": "thecontextnew",
        }
        if(!emailWhitelist[user.email as string]) {
          alert("You are not allowed to use this app");
          setUser(null);
          logout();
        } else {
          let workspace: Workspace = workspaces[emailWhitelist[user.email as string]];
          const workspaceRef = getRef('workspaces/'+workspace.id+'/name');
          onValue(workspaceRef, async (snapshot) => {
            const data = snapshot.val();
            if(!data) {
              await addWorkspace(workspace);
            }
            joinWorkspace(workspace, user as User);
            setActiveWorkspace(workspace);
            setReady(true);
          }, (error) => {
            console.error(error);
          });
        }
      } else {
        setUser(null)
        setActiveWorkspace(null);
        navigate('/');
        setReady(true);
      }
    });
  }, []);

  const location = useLocation();

  useEffect(() => {
    if(!location.pathname.includes("/chat")) {
      setInboxVisible(false);
    }
  }, [location]);

  const { modal, isOpen: isModalOpen, closeModal } = useModalContext();
  const { isMobileResolution, isSidebarOpen } = useLayoutContext();
  const isChatRoute =
    ["chat", "chats", "inbox"].some((p) => location.pathname.includes("/"+p));
  return (
    <div className="App h-full flex items-stretch bg-gray-10">
      {user?.displayName && <Sidebar /> }
      <div className={classNames(
        "contentarea bg-gray-9 flex flex-grow w-full border-l border-l-gray-7",
        )}>
        <div className={classNames(
          "transition-all rounded-lg overflow-x-hidden flex-shrink-0 flex-grow-0 relative",
          isMobileResolution
            ? "fixed top-0 h-full w-full"
            :  (chatListVisible ? "w-[497px]" : "w-0"),
          isMobileResolution && (activeChatId || !isChatRoute) && "-left-[100%]",
          isMobileResolution && !activeChatId && isChatRoute && "left-0",

        )}>
          <ChatList/>
        </div>
        <div className={
          classNames(
            "modal fixed inset-0 z-50",
            isModalOpen ? "display-block" : "display-none"
          )}>
          <div className={
            classNames(
              "backdrop fixed left-0 top-0 w-full h-full bg-black bg-opacity-50 transition-opacity duration-300",
              isModalOpen ? "opacity-100" : "opacity-0"
            )}
               onClick={() => closeModal()}></div>
          <div className={classNames(
            "modal-main rounded-md transition-all duration-300",
            isModalOpen ? "opacity-100" : "opacity-0"
          )}>
            <div className="modal-body min-w-full p-6">{modal}</div>
          </div>
        </div>
        <div className={
          classNames(
            "main border-l border-l-gray-7 bg-white flex-grow overflow-y-auto h-full overflow-x-hidden z-[1]",
            isMobileResolution ? "w-full fixed top-0 left-0 transition-all" : "relative",
            isMobileResolution && (
              activeChatId || !isChatRoute ? "opacity-100 pointer-events-auto" :  "opacity-0 pointer-events-none"
            )
          )
        }>
          {ready ? (
            <Routes>
              <Route path="/" element={<Login/>}/>
              <Route path="/inbox" element={<InboxPage/>}/>
              <Route path="/dm/:id" element={<Dm />}/>
              <Route path="/group/:id" element={<Group/>}/>
              <Route path="/chats/:id" element={<Chats />}/>
              <Route path="/group/:groupId/compose" element={<Compose/>}/>
              <Route path="/compose" element={<Compose/>}/>
              <Route path="/chat/:chatId" element={<ChatPage/>}/>
              <Route path="/inbox/chat/:chatId" element={<InboxPage/>}/>
              <Route path="/chats/:id/chat/:chatId" element={<Chats />}/>
              <Route path="/test" element={<Test/>}/>
              <Route path="/function" element={<FunctionCaller/>}/>
              <Route path="/pings" element={<Pings/>}/>
            </Routes>
          ) : (
            <div>Loading...</div>
          )}

        </div>
      </div>
    </div>
  );
}

export default App;
